import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { directory, orgchart, programs } from '@cwos/api-client';
import { Button, ConsequenceNote, Pill, SuccessEmpty } from '@cwos/ui-admin';
import { mayListWorkers, usePermissions } from '../permissions.js';
import { authorityGate, gateOpen, gateSentence } from '../authorityGate.js';
import { writeRefusalMessage } from '../ceilingRefusal.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { pickerState } from '../pickerState.js';
import { localDayString } from '../../lib/timeFormat.js';

/**
 * EDITING THE CHART — relationships, the customer's own relationship types, and
 * teams (§O24).
 *
 * ===========================================================================
 * AN EDGE IS AN AUTHORITY DECISION, NOT A LINE ON A DIAGRAM
 * ===========================================================================
 * A reporting relationship is what routes work and confers chain-of-command
 * authority — and only when it is AUTHORITY-BEARING. The named platform kinds
 * always are. A customer-defined type is only authority-bearing if the customer
 * said so when they defined it, which is why `authority_bearing` exists at all:
 * so an informational edge ("mentor", "buddy") does not silently become a chain
 * of command.
 *
 * The type-definition form therefore states the consequence of that checkbox in
 * words before it is ticked, and the type list renders the answer for every
 * existing type rather than leaving it implicit.
 *
 * ===========================================================================
 * A TEAM IS A SCOPE, AND IT IS NOW A LIST
 * ===========================================================================
 * `team` is one of the four `RelationshipScopeType`s. For a long time creating
 * one was all this product could do: there was no read route, so this screen
 * confirmed the uuid it had just created and told the administrator to write it
 * down, and the scope field on the relationship form was a text box asking for
 * that uuid back.
 *
 * `GET /orgchart/teams` closed that. The scope control is a PICKER, and the
 * four states of `pickerState` decide what it says when it cannot offer one:
 * loading, refused (a reader without `orgchart:read`), failed, and an
 * organization that genuinely has no teams are four different sentences, not
 * one empty dropdown. The id field survives underneath as the fallback for the
 * three of those that are not "you have no teams" — an administrator who can
 * see a uuid elsewhere is not blocked by a list this screen could not fetch.
 *
 * RETIRED TEAMS ARE LISTED AND NOT OFFERED. The route sends them so an existing
 * edge can still be resolved to a name; a NEW edge should not be created
 * against one, so they are shown in the team list marked retired and left out
 * of the picker.
 */
export function ChartEditing() {
  const permissions = usePermissions();
  const qc = useQueryClient();

  const manageGate = authorityGate(permissions, 'orgchart:manage');
  const mayManage = gateOpen(manageGate);
  const manageNote = gateSentence(manageGate, 'change this organization’s chart');

  const canReadDirectory = mayListWorkers(permissions);
  const workers = useQuery({
    queryKey: ['directory', 'workers', 'for-orgchart'],
    queryFn: () => directory.workers(),
    enabled: canReadDirectory,
  });
  const candidates = (workers.data ?? []).filter((w) => w.userId);

  const types = useQuery({
    queryKey: ['orgchart', 'relationship-types'],
    queryFn: () => orgchart.relationshipTypes(),
  });

  // `GET /orgchart/teams` takes `orgchart:read`, which is a strictly weaker key
  // than the `orgchart:manage` this screen's writes need — so the query is
  // gated on its OWN key rather than on the page's. A reader who may see the
  // chart and not change it still gets the list.
  const readGate = authorityGate(permissions, 'orgchart:read');
  const mayRead = gateOpen(readGate);
  const teams = useQuery({
    queryKey: ['orgchart', 'teams'],
    queryFn: () => orgchart.teams(),
    enabled: mayRead,
  });
  const liveTeams = (teams.data ?? []).filter((t) => t.retiredAt === null);

  /**
   * The program list, for the program-scope picker.
   *
   * Gated on `program:read` from a RESOLVED set. `has()` is true for a
   * program-confined grant, which is right: `GET /programs` narrows to the
   * grant and answers 200 rather than refusing, so a scoped reader gets the
   * programs they can actually scope a relationship to.
   */
  const programList = useQuery({
    queryKey: ['programs', 'for-orgchart'],
    queryFn: () => programs.list(),
    enabled: permissions.has('program:read'),
  });

  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  // --- new relationship ---------------------------------------------------
  const [subordinateUserId, setSubordinateUserId] = useState('');
  const [managerUserId, setManagerUserId] = useState('');
  const [kind, setKind] = useState<orgchart.RelationshipKind>('organizational_manager');
  const [typeKey, setTypeKey] = useState('');
  const [scopeType, setScopeType] = useState<orgchart.RelationshipScopeType>('org');
  const [scopeId, setScopeId] = useState('');
  // Default effective date is the viewer's LOCAL today — the UTC date here
  // made every evening's new relationship start tomorrow.
  const [effectiveFrom, setEffectiveFrom] = useState(() => localDayString(new Date()));

  const createRelationship = useMutation({
    mutationFn: () =>
      orgchart.createRelationship({
        subordinateUserId,
        managerUserId,
        relationshipKind: kind,
        relationshipTypeKey: kind === 'custom' ? typeKey : undefined,
        scopeType,
        scopeId: scopeType === 'org' ? null : scopeId,
        effectiveFrom,
      }),
    onSuccess: (edge) => {
      setError(null);
      setCreated(`Reporting relationship recorded, effective ${edge.effectiveFrom}.`);
      void qc.invalidateQueries({ queryKey: ['orgchart'] });
    },
    onError: (err: unknown) =>
      setError(writeRefusalMessage(err, 'We couldn’t record that relationship.')),
  });

  // --- new relationship type ---------------------------------------------
  const [newTypeKey, setNewTypeKey] = useState('');
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeAuthority, setNewTypeAuthority] = useState(false);

  const createType = useMutation({
    mutationFn: () =>
      orgchart.createRelationshipType({
        key: newTypeKey.trim(),
        name: newTypeName.trim(),
        authorityBearing: newTypeAuthority,
      }),
    onSuccess: (type) => {
      setError(null);
      setCreated(
        `Relationship type “${type.name}” defined. It ${type.authorityBearing ? 'DOES route work and confer authority' : 'is informational and routes no work'}.`,
      );
      setNewTypeKey('');
      setNewTypeName('');
      setNewTypeAuthority(false);
      void qc.invalidateQueries({ queryKey: ['orgchart', 'relationship-types'] });
    },
    onError: (err: unknown) =>
      setError(writeRefusalMessage(err, 'We couldn’t define that relationship type.')),
  });

  // --- new team -----------------------------------------------------------
  const [teamKey, setTeamKey] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamProgramId, setTeamProgramId] = useState('');

  const createTeam = useMutation({
    mutationFn: () =>
      orgchart.createTeam({
        key: teamKey.trim(),
        name: teamName.trim(),
        programId: teamProgramId === '' ? null : teamProgramId,
      }),
    onSuccess: (team) => {
      setError(null);
      setCreated(`Team “${team.name}” created. It is in the list below and in the scope picker.`);
      setTeamKey('');
      setTeamName('');
      void qc.invalidateQueries({ queryKey: ['orgchart', 'teams'] });
    },
    onError: (err: unknown) => setError(writeRefusalMessage(err, 'We couldn’t create that team.')),
  });

  const rosterWithheld = !permissions.isLoading && !permissions.isError && !canReadDirectory;
  const picker = pickerState(
    rosterWithheld
      ? { kind: 'refused' }
      : listPhase(
          permissions.isLoading || permissions.isError
            ? { isLoading: true, isError: false, error: null, data: undefined }
            : workers,
          candidates.length,
        ),
    'people',
    'Choose someone…',
  );

  const teamPicker = pickerState(
    !mayRead ? { kind: 'refused' } : listPhase(teams, liveTeams.length),
    'teams',
    'Choose a team…',
  );
  const scopeNeedsId = scopeType !== 'org';
  const relationshipReady =
    subordinateUserId !== '' &&
    managerUserId !== '' &&
    subordinateUserId !== managerUserId &&
    (kind !== 'custom' || typeKey !== '') &&
    (!scopeNeedsId || scopeId.trim() !== '');

  return (
    <div className="rn-col">
      <header className="rn-col">
        <h2>Relationships, types and teams</h2>
        <p className="rn-muted">
          The structure the rest of the product routes work through. A person can have several
          managers at once — an organizational manager and a program supervisor are different
          questions with different answers — so adding an edge never replaces one.
        </p>
      </header>

      {manageNote ? (
        <p className="rn-muted" role="status" data-manage-gate={manageGate.kind}>
          {manageNote}
        </p>
      ) : null}

      {error ? (
        <p className="rn-card" role="alert" data-chart-error="true">
          {error}
        </p>
      ) : null}
      {created ? (
        <p className="rn-card" role="status" data-chart-created="true">
          {created}
        </p>
      ) : null}

      <section className="rn-card rn-col" aria-label="Add a reporting relationship">
        <h3>Add a reporting relationship</h3>
        <div className="rn-row">
          <label>
            Person
            <select
              value={subordinateUserId}
              disabled={picker.disabled}
              onChange={(e) => setSubordinateUserId(e.target.value)}
            >
              <option value="">{picker.placeholder}</option>
              {candidates.map((w) => (
                <option key={w.userId!} value={w.userId!}>
                  {w.displayName}
                </option>
              ))}
            </select>
          </label>
          <label>
            Reports to
            <select
              value={managerUserId}
              disabled={picker.disabled}
              onChange={(e) => setManagerUserId(e.target.value)}
            >
              <option value="">{picker.placeholder}</option>
              {candidates
                .filter((w) => w.userId !== subordinateUserId)
                .map((w) => (
                  <option key={w.userId!} value={w.userId!}>
                    {w.displayName}
                  </option>
                ))}
            </select>
          </label>
        </div>
        {picker.note ? (
          <p className="rn-muted" role="status">
            {picker.note}
          </p>
        ) : null}

        <div className="rn-row">
          <label>
            In what capacity
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as orgchart.RelationshipKind)}
            >
              {(Object.keys(orgchart.RELATIONSHIP_KIND_LABEL) as orgchart.RelationshipKind[]).map(
                (k) => (
                  <option key={k} value={k}>
                    {orgchart.RELATIONSHIP_KIND_LABEL[k]}
                  </option>
                ),
              )}
            </select>
          </label>
          {kind === 'custom' ? (
            <label>
              Which of your own types
              <select value={typeKey} onChange={(e) => setTypeKey(e.target.value)}>
                <option value="">Choose a type…</option>
                {(types.data ?? [])
                  .filter((t) => t.retiredAt === null)
                  .map((t) => (
                    <option key={t.id} value={t.key}>
                      {t.name}
                      {t.authorityBearing ? '' : ' (informational)'}
                    </option>
                  ))}
              </select>
            </label>
          ) : null}
        </div>

        <div className="rn-row">
          <label>
            Over what
            <select
              value={scopeType}
              onChange={(e) => {
                setScopeType(e.target.value as orgchart.RelationshipScopeType);
                setScopeId('');
              }}
            >
              {(Object.keys(orgchart.SCOPE_TYPE_LABEL) as orgchart.RelationshipScopeType[]).map(
                (s) => (
                  <option key={s} value={s}>
                    {orgchart.SCOPE_TYPE_LABEL[s]}
                  </option>
                ),
              )}
            </select>
          </label>
          {scopeType === 'program' ? (
            <label>
              Program
              <select value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
                <option value="">Choose a program…</option>
                {(programList.data?.programs ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          ) : scopeType === 'team' ? (
            /*
              THE PICKER, and the id field that survives beside it.

              `pickerState` decides which of four things happened when the list
              is not a list: still loading, withheld from this reader, failed to
              load, or genuinely empty. Only the last is a claim about the
              ORGANIZATION, and only in that case does the fallback field go
              away — because typing a uuid for a team that does not exist cannot
              help anybody. In the other three the field stays, so a reader who
              knows the id is not blocked by a fetch this screen lost.
            */
            <>
              <label>
                Team
                <select
                  value={scopeId}
                  disabled={teamPicker.disabled}
                  onChange={(e) => setScopeId(e.target.value)}
                  data-team-picker={teamPicker.disabled ? 'inert' : 'live'}
                >
                  <option value="">{teamPicker.placeholder}</option>
                  {liveTeams.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
              {teamPicker.note ? (
                <p className="rn-muted" role="status" data-team-picker-note="true">
                  {teamPicker.note}
                </p>
              ) : null}
              {teamPicker.disabled && teams.data?.length !== 0 ? (
                <label>
                  Team id
                  <input
                    type="text"
                    value={scopeId}
                    onChange={(e) => setScopeId(e.target.value)}
                    placeholder="00000000-0000-0000-0000-000000000000"
                  />
                </label>
              ) : null}
            </>
          ) : scopeNeedsId ? (
            /*
              Location still has no picker here: the location list this screen
              would need is not in its reach. An id field is honest about that —
              it says the value is an identifier rather than dressing a
              free-text box up as a chooser.
            */
            <label>
              Location id
              <input
                type="text"
                value={scopeId}
                onChange={(e) => setScopeId(e.target.value)}
                placeholder="00000000-0000-0000-0000-000000000000"
              />
            </label>
          ) : null}
          <label>
            Effective from
            <input
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </label>
        </div>

        <ConsequenceNote text="An authority-bearing relationship routes work and confers chain-of-command authority from the date it starts. It does not replace any manager this person already has — the chart holds several at once, on purpose." />

        <Button
          variant="primary"
          disabled={!mayManage || !relationshipReady || createRelationship.isPending}
          onClick={() => createRelationship.mutate()}
        >
          {createRelationship.isPending ? 'Recording…' : 'Record this relationship'}
        </Button>
      </section>

      <section className="rn-card rn-col" aria-label="Relationship types">
        <h3>Your own relationship types</h3>
        <ListState
          phase={listPhase(types, types.data?.length)}
          surface="table"
          rows={3}
          failedMessage="We couldn’t load your relationship types just now."
          onRetry={() => void types.refetch()}
          empty={
            <SuccessEmpty
              heading="None defined"
              message="Your organization has defined no relationship types of its own. The platform's named kinds are still available — this is a real answer about your own types."
            />
          }
        >
          <ul className="rn-list">
            {(types.data ?? []).map((t) => (
              <li key={t.id} data-relationship-type={t.key}>
                <strong>{t.name}</strong> <code>{t.key}</code>
                <Pill variant={t.authorityBearing ? 'good' : 'plain'}>
                  {t.authorityBearing ? 'Routes work and confers authority' : 'Informational only'}
                </Pill>
                {t.retiredAt ? <span className="rn-muted"> · retired {t.retiredAt}</span> : null}
              </li>
            ))}
          </ul>
        </ListState>

        <h4>Define a new type</h4>
        <div className="rn-row">
          <label>
            Key
            <input
              type="text"
              value={newTypeKey}
              onChange={(e) => setNewTypeKey(e.target.value)}
              placeholder="program-lead"
            />
          </label>
          <label>
            Name
            <input
              type="text"
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              placeholder="Program Lead"
            />
          </label>
        </div>
        <label>
          <input
            type="checkbox"
            checked={newTypeAuthority}
            onChange={(e) => setNewTypeAuthority(e.target.checked)}
          />{' '}
          This relationship routes work and confers authority
        </label>
        <ConsequenceNote text="Leave that unticked and the relationship is informational: it appears on the chart and routes nothing. Tick it and every edge of this type becomes a chain of command — coverage evaluation counts it, and work assigned to “their manager” goes to whoever holds it." />
        <Button
          variant="secondary"
          disabled={
            !mayManage ||
            newTypeKey.trim() === '' ||
            newTypeName.trim() === '' ||
            createType.isPending
          }
          onClick={() => createType.mutate()}
        >
          {createType.isPending ? 'Defining…' : 'Define this type'}
        </Button>
      </section>

      <section className="rn-card rn-col" aria-label="Teams">
        <h3>Teams</h3>
        <p className="rn-muted">
          A team is one of the four scopes a relationship can be held at. The ones this organization
          already has are listed below, and the live ones are offered as the scope of a new
          relationship.
        </p>
        <ListState
          phase={!mayRead ? { kind: 'refused' } : listPhase(teams, teams.data?.length ?? 0)}
          surface="table"
          failedMessage="The team list did not load. This section is empty because of that, not because your organization has no teams."
          empty={
            <SuccessEmpty
              heading="No teams yet"
              message="The server listed this organization’s teams and there were none. Create the first one below."
            />
          }
        >
          <ul className="rn-list" data-team-list="true">
            {(teams.data ?? []).map((t) => (
              <li key={t.id} data-team={t.key}>
                <strong>{t.name}</strong> <code>{t.key}</code>
                {t.retiredAt ? (
                  <Pill variant="plain">
                    Retired — still resolves an existing edge, not offered for a new one
                  </Pill>
                ) : null}
              </li>
            ))}
          </ul>
        </ListState>
        <div className="rn-row">
          <label>
            Key
            <input
              type="text"
              value={teamKey}
              onChange={(e) => setTeamKey(e.target.value)}
              placeholder="intake"
            />
          </label>
          <label>
            Name
            <input
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Intake team"
            />
          </label>
          <label>
            Program (optional)
            <select value={teamProgramId} onChange={(e) => setTeamProgramId(e.target.value)}>
              <option value="">No program</option>
              {(programList.data?.programs ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <Button
          variant="secondary"
          disabled={
            !mayManage || teamKey.trim() === '' || teamName.trim() === '' || createTeam.isPending
          }
          onClick={() => createTeam.mutate()}
        >
          {createTeam.isPending ? 'Creating…' : 'Create this team'}
        </Button>
      </section>
    </div>
  );
}
